<template>
  <div class="particle-area">
    <div class="hero-bg"></div>
    <div class="sphere-container" ref="container"></div>
  </div>
</template>

<script>
import * as THREE from 'three';
import { gsap } from 'gsap';
import { onMounted, onUnmounted, ref } from 'vue';

export default {
  name: 'ParticleSphere',
  props: {
    heroTag: {
      type: Array,
      required: true
    }
  },
  setup(props) {
    const container = ref(null);
    let scene, camera, renderer, pointsObj, geometry, material;
    let positions, sphereCoords;
    let currentStage = -1;
    let currentWordIndex = 0;
    let animationFrameId = null;
    let isVisible = false;
    let nextStageTimeoutId = null;
    const colorSchemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const PARTICLE_COUNT = 8000;
    const SPHERE_RADIUS = 2;

    // Helper: Convert CSS hex color to Three.js hex
const getThemeColor = () => {
  const accentHex = getComputedStyle(document.documentElement)
    .getPropertyValue('--accent-color')
    .trim();

  // Create a Three.js Color object from the CSS hex string
  // This ensures it is correctly interpreted as sRGB
  const color = new THREE.Color(accentHex);
  color.convertSRGBToLinear();

  return color;
};

const updateThemeColor = () => {
  if (material) material.color.copy(getThemeColor());
};


    // Initialize Three.js scene
    const initScene = () => {
      scene = new THREE.Scene();
      camera = new THREE.PerspectiveCamera(
        60,
        container.value.clientWidth / container.value.clientHeight,
        0.1,
        1000
      );
      camera.position.z = 6;

      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(container.value.clientWidth, container.value.clientHeight);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setClearColor(0x000000, 0);
      container.value.appendChild(renderer.domElement);

      // Create glow texture
      const glowTexture = createGlowTexture();

      // Initialize particle positions
      positions = new Float32Array(PARTICLE_COUNT * 3);
      sphereCoords = new Float32Array(PARTICLE_COUNT * 3);
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const phi = Math.acos(-1 + (2 * i) / PARTICLE_COUNT);
        const theta = Math.sqrt(PARTICLE_COUNT * Math.PI) * phi;
        sphereCoords[i * 3] = SPHERE_RADIUS * Math.cos(theta) * Math.sin(phi);
        sphereCoords[i * 3 + 1] = SPHERE_RADIUS * Math.sin(theta) * Math.sin(phi);
        sphereCoords[i * 3 + 2] = SPHERE_RADIUS * Math.cos(phi);
        positions[i * 3] = sphereCoords[i * 3];
        positions[i * 3 + 1] = sphereCoords[i * 3 + 1];
        positions[i * 3 + 2] = sphereCoords[i * 3 + 2];
      }

      // Create particle system with dynamic color
      geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      material = new THREE.PointsMaterial({
        size: 0.08,
        map: glowTexture,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        color: getThemeColor()
      });
      pointsObj = new THREE.Points(geometry, material);
      scene.add(pointsObj);

      // Start animation
      setupIntersectionObserver();
    };

    // Create glow texture
    const createGlowTexture = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 64; canvas.height = 64;
      const ctx = canvas.getContext('2d');
      const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
      gradient.addColorStop(0.2, 'rgba(122, 166, 109, 0.8)');
      gradient.addColorStop(0.5, 'rgba(45, 58, 58, 0.3)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 64, 64);
      return new THREE.CanvasTexture(canvas);
    };

    // Generate particle positions for text
    const getCoordsForText = (text) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 800;
      canvas.height = 300;
      ctx.fillStyle = 'white';
      let fontSize = text.length > 3 ? 90 : 120;
      ctx.font = `bold ${fontSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, canvas.width/2, canvas.height/2);

      const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      const points = [];

      for (let y = 0; y < canvas.height; y += 3) {
        for (let x = 0; x < canvas.width; x += 3) {
          const i = (y * canvas.width + x) * 4;
          if (data[i + 3] > 128) {
            points.push({
              x: (x - canvas.width/2) / 60,
              y: -(y - canvas.height/2) / 60
            });
          }
        }
      }

      const target = new Float32Array(PARTICLE_COUNT * 3);
      if (points.length === 0) return target;

      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const p = points[i % points.length];
        target[i * 3] = p.x + (Math.random() - 0.5) * 0.03;
        target[i * 3 + 1] = p.y + (Math.random() - 0.5) * 0.03;
        target[i * 3 + 2] = (Math.random() - 0.5) * 0.08;
      }
      return target;
    };

    // Morph to next stage
    const nextStage = () => {
      if (props.heroTag.length === 0) return;

      let targetCoords;
      if (currentStage === -1) {
        currentStage = currentWordIndex;
        targetCoords = getCoordsForText(props.heroTag[currentWordIndex]);
      } else {
        currentStage = -1;
        currentWordIndex = (currentWordIndex + 1) % props.heroTag.length;
        targetCoords = sphereCoords;
      }

      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const idx = i * 3;
        gsap.to(positions, {
          duration: 1.8,
          ease: "expo.inOut",
          delay: Math.random() * 0.4,
          [idx]: targetCoords[idx],
          [idx + 1]: targetCoords[idx + 1],
          [idx + 2]: targetCoords[idx + 2],
          onUpdate: () => {
            if (i === 0) geometry.attributes.position.needsUpdate = true;
          }
        });
      }

      if (isVisible) {
        nextStageTimeoutId = setTimeout(nextStage, 4000);
      }
    };

    // Animation loop
    const animate = () => {
      if (!isVisible) return;
      animationFrameId = requestAnimationFrame(animate);

      if (currentStage === -1) {
        pointsObj.rotation.y += 0.004;
        pointsObj.rotation.x += 0.002;
      } else {
        pointsObj.rotation.y *= 0.95;
        pointsObj.rotation.x *= 0.95;
      }

      renderer.render(scene, camera);
    };

    // Start animation
    const startAnimation = () => {
      if (!isVisible) return;
      animate();
      nextStageTimeoutId = setTimeout(nextStage, 2000);
    };

    // Stop animation
    const stopAnimation = () => {
      cancelAnimationFrame(animationFrameId);
      clearTimeout(nextStageTimeoutId);
    };

    // Setup Intersection Observer
    const setupIntersectionObserver = () => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            isVisible = entry.isIntersecting;
            if (isVisible) {
              startAnimation();
            } else {
              stopAnimation();
            }
          });
        },
        { threshold: 0.1 }
      );
      observer.observe(container.value);
    };

    // Handle window resize
    const onWindowResize = () => {
      camera.aspect = container.value.clientWidth / container.value.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(container.value.clientWidth, container.value.clientHeight);
    };

    // Lifecycle hooks
    onMounted(() => {
      colorSchemeQuery.addEventListener('change', updateThemeColor);
      initScene();
      window.addEventListener('resize', onWindowResize);
    });

    onUnmounted(() => {
      colorSchemeQuery.removeEventListener('change', updateThemeColor);
      window.removeEventListener('resize', onWindowResize);
      stopAnimation();
      if (container.value && renderer) {
        container.value.removeChild(renderer.domElement);
      }
    });

    return { container };
  }
};
</script>
